import React, { useEffect, useState } from 'react';
import './EventPopup.css';

const EventPopup = ({ events, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    setTimeout(() => setIsVisible(true), 50);

    // Prevent body scroll when popup is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!events || events.length === 0) return null;

  const event = events[currentIndex];

  const handleViewEvent = () => {
    if (event.eventUrl) {
      window.open(event.eventUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentIndex < events.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className={`event-popup-overlay ${isVisible ? 'visible' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`event-popup-card ${isVisible ? 'visible' : ''}`}>
        {/* Close Button */}
        <button
          className="event-popup-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Event Image */}
        <div className="event-popup-image-wrapper">
          <img
            src={event.imageUrl}
            alt={event.title}
            className="event-popup-image"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/500x300?text=Event';
            }}
          />
          <div className="event-popup-image-overlay"></div>

          {/* Navigation arrows for multiple events */}
          {events.length > 1 && (
            <>
              <button
                className={`event-popup-nav event-popup-nav-prev ${currentIndex === 0 ? 'disabled' : ''}`}
                onClick={handlePrev}
                disabled={currentIndex === 0}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button
                className={`event-popup-nav event-popup-nav-next ${currentIndex === events.length - 1 ? 'disabled' : ''}`}
                onClick={handleNext}
                disabled={currentIndex === events.length - 1}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}
        </div>

        {/* Event Content */}
        <div className="event-popup-content">
          {/* Special Event Badge */}
          <div className="event-popup-badge">
            <i className="bi bi-stars me-1"></i>
            Special Event
          </div>

          {/* Date Badge */}
          <div className="event-popup-date">
            <i className="bi bi-calendar-event me-2"></i>
            {event.dateFormatted}
            {event.time && (
              <span className="event-popup-time">
                <i className="bi bi-clock ms-3 me-1"></i>
                {event.time}
              </span>
            )}
          </div>

          {/* Title */}
          <h2 className="event-popup-title">{event.title}</h2>

          {/* Description */}
          <p className="event-popup-description">
            {event.shortDescription}
          </p>

          {/* Category */}
          {event.category && (
            <div className="event-popup-category">
              <i className="bi bi-tag me-1"></i>
              {event.category}
            </div>
          )}

          {/* Pagination dots */}
          {events.length > 1 && (
            <div className="event-popup-dots">
              {events.map((_, index) => (
                <span
                  key={index}
                  className={`event-popup-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                />
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="event-popup-actions">
            <button
              className="event-popup-btn event-popup-btn-primary"
              onClick={handleViewEvent}
            >
              <i className="bi bi-box-arrow-up-right me-2"></i>
              View Event Details
            </button>
            <button
              className="event-popup-btn event-popup-btn-secondary"
              onClick={handleClose}
            >
              Continue Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPopup;
